/*
 * WHAT A REPLAY CAN TEST — worked out from the model alone, before any history.
 *
 * A replay can only hand a model two kinds of event: `symbolValueChanged` for a
 * controller symbol that its wiring connects, and `valueChanged` for another
 * model's output (and, in a group, for a port of another machine). On
 * top of those, the model can raise and send events to itself, and the engine
 * produces `done.state.*` and `error.*`. Anything else a transition waits for —
 * a command from the app, an AI call finishing, a misspelt event name — never
 * arrives in a replay, whatever window is chosen. So some states and edges can
 * be known in advance to be untestable, and a person should hear that before
 * they spend a run discovering "never entered".
 *
 * This is a reachability pass over the document, not a simulation. Conditions
 * (`cond`) are not evaluated: an edge whose event can arrive counts as able to
 * fire, because whether its condition is ever true depends on the data. The
 * answer is therefore "can history reach this at all", never "will it".
 *
 * The parser takes an Element-shaped interface so the rules can be tested with
 * plain objects, without a DOM.
 */
import { classifyBindingTarget } from './binding-target';

/** The events a replay sends for recorded values — the same as the live runner. */
export const REPLAY_INPUT_EVENTS = ['symbolValueChanged', 'valueChanged'] as const;

// ── the document, as the pass needs it ──────────────────────────────────────

export interface CoverageTransition {
  /** Space-separated descriptor tokens, or '' for an eventless transition. */
  event: string;
  /** Space-separated target ids, or '' for a targetless transition. */
  target: string;
  /** Events this transition's own body raises or sends to the model itself. */
  sends: string[];
}

export interface CoverageNode {
  id: string;
  type: 'scxml' | 'state' | 'parallel' | 'final' | 'history';
  /** Space-separated ids from `initial="…"` or an `<initial>` child. */
  initial: string;
  children: CoverageNode[];
  transitions: CoverageTransition[];
  /** Events raised or sent by `<onentry>` and `<onexit>`. */
  sends: string[];
}

/** The slice of the DOM Element interface the parser reads. */
export interface ElementLike {
  tagName: string;
  getAttribute(name: string): string | null;
  readonly children: ArrayLike<ElementLike>;
}

const STATE_TAGS = new Set(['state', 'parallel', 'final', 'history']);

/** Local name, lower-cased — a namespaced document reports `scxml:state`. */
const tag = (el: ElementLike) => el.tagName.toLowerCase().replace(/^.*:/, '');
const kids = (el: ElementLike) => Array.from(el.children);

/**
 * The events a block of executable content sends to the model itself, found
 * anywhere inside it (`<if>`, `<foreach>` included). A `<send>` with an external
 * target goes somewhere else and is not counted; `eventexpr` is computed at run
 * time and cannot be named, so it is not counted either.
 */
function collectSends(el: ElementLike, out: string[]): void {
  for (const c of kids(el)) {
    const t = tag(c);
    if (t === 'raise') {
      const ev = c.getAttribute('event');
      if (ev) out.push(ev);
    } else if (t === 'send') {
      const ev = c.getAttribute('event');
      const target = c.getAttribute('target') ?? '';
      if (ev && (target === '' || target === '#_internal')) out.push(ev);
    }
    collectSends(c, out);
  }
}

export function coverageTree(el: ElementLike): CoverageNode {
  let counter = 0;
  const walk = (e: ElementLike): CoverageNode => {
    const t = tag(e);
    const type = (t === 'scxml' ? 'scxml' : t) as CoverageNode['type'];
    const node: CoverageNode = {
      id: e.getAttribute('id') || (type === 'scxml' ? 'scxml' : `${type}_${counter++}`),
      type,
      initial: e.getAttribute('initial') ?? '',
      children: [],
      transitions: [],
      sends: [],
    };
    for (const c of kids(e)) {
      const ct = tag(c);
      if (STATE_TAGS.has(ct)) {
        node.children.push(walk(c));
      } else if (ct === 'transition') {
        const sends: string[] = [];
        collectSends(c, sends);
        node.transitions.push({ event: (c.getAttribute('event') ?? '').trim(), target: (c.getAttribute('target') ?? '').trim(), sends });
      } else if (ct === 'initial') {
        const tr = kids(c).find((k) => tag(k) === 'transition');
        if (tr && !node.initial) node.initial = tr.getAttribute('target') ?? '';
      } else if (ct === 'onentry' || ct === 'onexit') {
        collectSends(c, node.sends);
      }
    }
    return node;
  };
  return walk(el);
}

/** Parse SCXML text with the browser's parser. Null when it is not well-formed. */
export function parseCoverageTree(scxml: string): CoverageNode | null {
  if (!scxml.trim() || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(scxml, 'application/xml');
  if (doc.querySelector('parsererror') || !doc.documentElement) return null;
  return coverageTree(doc.documentElement);
}

/**
 * Every PLC symbol a model can write, once each: the paths its wiring drives, the root's outputs
 * and each child's. A model writes nothing else outside, so the list is the whole story.
 */
export function plcWritePaths(m: {
  wiring?: { outputs: ReadonlyArray<{ path: string }> };
  children?: ReadonlyArray<{ outputs: ReadonlyArray<{ path: string }> }>;
}): string[] {
  const paths = [...(m.wiring?.outputs ?? []), ...(m.children ?? []).flatMap((c) => c.outputs)].map((o) => o.path).filter(Boolean);
  return [...new Set(paths)];
}

// ── the answer ──────────────────────────────────────────────────────────────

/** Why an event never arrives in a replay. */
export type MissingEventWhy =
  /** Sent only for controller symbols, and this model reads none. */
  | 'no-controller-inputs'
  /** Sent only for other models' outputs, and this model reads none. */
  | 'no-model-inputs'
  /** `vitesse.*` — pause, resume, reset or setVariable from the app. */
  | 'app'
  /** `done.invoke.*` — an AI call finishing; a replay makes no AI calls. */
  | 'ai'
  /** Nothing in a replay produces it: a command sent from outside, or a typo. */
  | 'outside';

export type Blocked =
  | { kind: 'event'; event: string; why: MissingEventWhy; suggestion: string | null }
  /** The edge's own state can never be active in a replay. */
  | { kind: 'source'; source: string };

export interface EdgeCoverage {
  source: string;
  target: string;
  event: string;
  fires: boolean;
  /** Set when `fires` is false. */
  blocked: Blocked | null;
}

export interface StateCoverage {
  id: string;
  reachable: boolean;
  /** For an unreachable state: every edge into it, and why each cannot fire.
   *  Empty means nothing in the document leads to it. */
  blockedBy: EdgeCoverage[];
}

export interface ReplayCoverage {
  /** Every declared state (history pseudo-states excluded), in document order. */
  states: StateCoverage[];
  /** Edges out of reachable states that can never fire. An edge into a state
   *  that cannot be reached is left out: that state's entry already explains it,
   *  and saying it twice reads as two problems. */
  deadEdges: EdgeCoverage[];
  /** The replay events this model can actually receive. */
  inputEvents: string[];
}

/** SCXML descriptor matching: a token matches the name exactly or as a dotted
 *  prefix (`error` matches `error.execution`), `*` matches anything, and a
 *  trailing `.*` is the same as the bare prefix. */
export function descriptorMatches(token: string, name: string): boolean {
  const tok = token.replace(/\.\*$/, '');
  return tok === '*' || tok === name || name.startsWith(`${tok}.`);
}

/** The replay event a misspelt token was probably meant to be — the same name
 *  in another case (`ValueChanged`), or the tail of one (`valuechanged` for
 *  `symbolValueChanged`). One the model can actually receive is preferred. */
function suggestionFor(token: string, deliverable: readonly string[]): string | null {
  const lower = token.toLowerCase();
  const near = REPLAY_INPUT_EVENTS.filter((k) => k !== token && (k.toLowerCase() === lower || k.toLowerCase().endsWith(lower)));
  return near.find((k) => deliverable.includes(k)) ?? near[0] ?? null;
}

/**
 * `inputPaths` are the outside paths the document's own inputs read. `ports` is true for a
 * group's root, which reads its children's ports: each change of one arrives as `valueChanged`.
 */
export function analyzeReplayCoverage(root: CoverageNode, inputPaths: readonly string[], ports = false): ReplayCoverage {
  const kinds = new Set(inputPaths.map((p) => classifyBindingTarget(p).kind));
  const inputEvents: string[] = [];
  if (kinds.has('controller')) inputEvents.push('symbolValueChanged');
  if (kinds.has('model') || ports) inputEvents.push('valueChanged');

  // Index the tree.
  const byId = new Map<string, CoverageNode>();
  const parent = new Map<CoverageNode, CoverageNode>();
  const order: CoverageNode[] = [];
  const index = (n: CoverageNode) => {
    byId.set(n.id, n);
    order.push(n);
    for (const c of n.children) {
      parent.set(c, n);
      index(c);
    }
  };
  index(root);

  const active = new Set<CoverageNode>();
  // The engine itself can always produce an execution error.
  const events = new Set<string>([...inputEvents, 'error.execution', 'error.communication']);
  const firing = new Set<CoverageTransition>();
  let changed = true;

  const addEvents = (names: readonly string[]) => {
    for (const e of names) {
      if (!events.has(e)) {
        events.add(e);
        changed = true;
      }
    }
  };

  /** Enter a state: its ancestors become active, and so does its default
   *  descent — the initial child of a compound, every region of a parallel. */
  const enter = (n: CoverageNode | undefined): void => {
    if (!n || active.has(n)) return;
    if (n.type === 'history') {
      // Restores some earlier configuration of the parent; the parent's own
      // default descent is the one a first visit takes.
      enter(parent.get(n));
      for (const t of n.transitions) for (const id of t.target.split(/\s+/).filter(Boolean)) enter(byId.get(id));
      return;
    }
    active.add(n);
    changed = true;
    addEvents(n.sends);
    // Ancestors become active too — and an ancestor that is a PARALLEL is
    // entered whole: SCXML enters every one of its regions, each by its own
    // default, not only the region on the path to `n`. An active ancestor's own
    // ancestors are already active, so the walk stops at the first one.
    let onPath: CoverageNode = n;
    for (let p = parent.get(n); p && !active.has(p); onPath = p, p = parent.get(p)) {
      active.add(p);
      addEvents(p.sends);
      if (p.type === 'parallel') for (const r of p.children) if (r !== onPath && r.type !== 'history') enter(r);
    }
    const regions = n.children.filter((c) => c.type !== 'history');
    if (n.type === 'parallel') {
      for (const c of regions) enter(c);
    } else if (regions.length) {
      const ids = n.initial.split(/\s+/).filter(Boolean);
      if (ids.length) for (const id of ids) enter(byId.get(id));
      else enter(regions[0]);
    }
  };

  enter(root);

  const canArrive = (descriptor: string) => {
    if (!descriptor) return true;
    for (const tok of descriptor.split(/\s+/)) {
      for (const e of events) if (descriptorMatches(tok, e)) return true;
    }
    return false;
  };

  while (changed) {
    changed = false;
    for (const n of order) {
      if (!active.has(n)) continue;
      // A final child finishing its parent: done.state.<parent>, and a parallel
      // whose every region can finish is done too.
      if (n.type === 'final') {
        const p = parent.get(n);
        if (p && p.type !== 'scxml') {
          addEvents([`done.state.${p.id}`]);
          const gp = parent.get(p);
          if (gp?.type === 'parallel' && gp.children.every((r) => r.children.some((c) => c.type === 'final' && active.has(c)))) {
            addEvents([`done.state.${gp.id}`]);
          }
        }
      }
      for (const t of n.transitions) {
        if (firing.has(t) || !canArrive(t.event)) continue;
        firing.add(t);
        changed = true;
        addEvents(t.sends);
        // Leaving a state runs its exit actions.
        addEvents(n.sends);
        for (const id of t.target.split(/\s+/).filter(Boolean)) enter(byId.get(id));
      }
    }
  }

  const why = (token: string): MissingEventWhy => {
    if (token.startsWith('vitesse.')) return 'app';
    if (token.startsWith('done.invoke')) return 'ai';
    if (token === 'symbolValueChanged') return 'no-controller-inputs';
    if (token === 'valueChanged') return 'no-model-inputs';
    return 'outside';
  };

  const edge = (source: CoverageNode, t: CoverageTransition): EdgeCoverage => {
    const fires = firing.has(t);
    let blocked: Blocked | null = null;
    if (!fires) {
      if (!active.has(source)) {
        blocked = { kind: 'source', source: source.id };
      } else {
        // The first token that names something — every token is equally unable to arrive.
        const token = t.event.split(/\s+/)[0];
        blocked = { kind: 'event', event: token, why: why(token), suggestion: suggestionFor(token, inputEvents) };
      }
    }
    return { source: source.id, target: t.target, event: t.event, fires, blocked };
  };

  const states: StateCoverage[] = [];
  const deadEdges: EdgeCoverage[] = [];
  for (const n of order) {
    if (n.type === 'scxml' || n.type === 'history') continue;
    const reachable = active.has(n);
    const blockedBy: EdgeCoverage[] = [];
    if (!reachable) {
      for (const src of order) {
        for (const t of src.transitions) {
          if (t.target.split(/\s+/).includes(n.id)) blockedBy.push(edge(src, t));
        }
      }
    }
    states.push({ id: n.id, reachable, blockedBy });
  }
  const explained = new Set(states.filter((st) => !st.reachable).map((st) => st.id));
  for (const n of order) {
    if (!active.has(n)) continue;
    for (const t of n.transitions) {
      if (firing.has(t)) continue;
      const targets = t.target.split(/\s+/).filter(Boolean);
      if (targets.length && targets.every((id) => explained.has(id))) continue;
      deadEdges.push(edge(n, t));
    }
  }
  return { states, deadEdges, inputEvents };
}
